/**
 * `/settings/dsar` — the managing-partner-only DSAR (subject-rights) fulfilment queue
 * (Sprint 7 T-7.14 / US-153). Renders under the `_partner-only` layout (managing_partner
 * ONLY — the same wall the audit-export screen rides; a `firm_admin` reaches `_settings`
 * but NOT this route). The queue itself is `dsar.read`; the three fulfilment affordances
 * mirror the SERVER gate exactly (CC-2) behind `dsar.manage`:
 *
 *  - EXPORT (access): a plain `<a href>` download of the PII-safe subject export — the
 *    browser sends the session cookie, exactly like the audit export (never a fetch).
 *  - RECTIFY (rectification): correct ONE allowlisted PII field (full_name | email).
 *  - ERASE (erasure): erase the subject's case-scoped data — REFUSED (409) by a legal
 *    hold or the retention floor. The guard refusal is surfaced in a `role="alert"`; a
 *    202 deferred response is surfaced as an approved-pending-sign-off status.
 *
 * Built on the design-system primitives (UI/UX Flow 31).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Pencil, Trash2 } from '../../components/ui/icons.ts';
import { useId, useState, type JSX } from 'react';

import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  Notice,
  PageHeader,
  Select,
  SkeletonText,
  Table,
  Td,
  Th,
  buttonClassName,
  type BadgeTone,
} from '../../components/ui/index.ts';
import {
  api,
  getErrorMessage,
  isApiError,
  type DsarRectifyField,
  type DsarRequest,
  type DsarStatus,
} from '../../lib/api.ts';
import { describeError, getRequestId, type DescribedError } from '../../lib/request-id.ts';
import { formatDate } from '../../lib/format/date.ts';
import { usePermission } from '../../lib/permissions/index.tsx';

import '../../styles/settings.css';

const DSAR_KEY = ['dsar', 'list'] as const;

const RECTIFY_FIELD_LABELS: Record<DsarRectifyField, string> = {
  full_name: 'Full name',
  email: 'Email',
};

const STATUS_TONES: Record<DsarStatus, BadgeTone> = {
  submitted: 'open',
  in_progress: 'in-progress',
  fulfilled: 'resolved',
  refused: 'danger',
};

/** "in_progress" → "In progress" (design-system.md §15: sentence case, precise terms). */
function humanise(token: string): string {
  const words = token.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** A human sentence for an erase refusal — surfaces the machine `details.reason` (CC-7). */
function eraseErrorMessage(err: unknown): string {
  if (isApiError(err) && err.status === 409) {
    const reason = err.details?.['reason'];
    if (reason === 'legal_hold')
      return 'Erasure refused — the subject has data under a legal hold (legal_hold).';
    if (reason === 'retention_floor')
      return 'Erasure refused — a statutory retention floor still applies (retention_floor).';
    if (reason === 'already_fulfilled')
      return 'This request has already been fulfilled (already_fulfilled).';
  }
  return getErrorMessage(err);
}

function subjectLabel(row: DsarRequest): string {
  return row.subject_email ?? row.subject_user_id;
}

export function DsarScreen(): JSX.Element {
  const queryClient = useQueryClient();
  const rectifyFormId = useId();
  // The fulfilment affordances mirror the server gate: dsar.manage (catalogue, CC-2).
  const canManage = usePermission('dsar.manage');

  const queue = useQuery({
    queryKey: DSAR_KEY,
    queryFn: () => api.dsar.list(),
  });

  const [rectifyFor, setRectifyFor] = useState<DsarRequest | null>(null);
  const [rectifyField, setRectifyField] = useState<DsarRectifyField>('full_name');
  const [rectifyValue, setRectifyValue] = useState('');
  const [eraseFor, setEraseFor] = useState<DsarRequest | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<DescribedError | null>(null);

  function resetBanners(): void {
    setStatus(null);
    setError(null);
  }

  function closeRectify(): void {
    setRectifyFor(null);
    setRectifyValue('');
  }

  const rectifyMutation = useMutation({
    mutationFn: (vars: { requestId: string; field: DsarRectifyField; value: string }) =>
      api.dsar.rectify(vars.requestId, vars.field, vars.value),
    onSuccess: async (updated, vars) => {
      setStatus(
        `Rectified ${vars.field} for the subject. Request marked ${humanise(updated.status).toLowerCase()}.`,
      );
      setError(null);
      closeRectify();
      await queryClient.invalidateQueries({ queryKey: DSAR_KEY });
    },
    onError: (err) => {
      setError(describeError(err));
      setStatus(null);
    },
  });

  const eraseMutation = useMutation({
    mutationFn: (requestId: string) => api.dsar.erase(requestId),
    onSuccess: async (updated) => {
      // 202 deferred lands as status `in_progress`; 200 erased lands as `fulfilled`.
      setStatus(
        updated.status === 'in_progress'
          ? 'Erasure approved — deferred pending sign-off.'
          : 'Subject data erased. Request fulfilled.',
      );
      setError(null);
      setEraseFor(null);
      await queryClient.invalidateQueries({ queryKey: DSAR_KEY });
    },
    onError: (err) => {
      // The 409 guard refusal (legal_hold / retention_floor) surfaces here — the human
      // reason replaces the generic message; the request id (F-39) still rides along.
      setError({ ...describeError(err), message: eraseErrorMessage(err) });
      setStatus(null);
      setEraseFor(null);
    },
  });

  const rows = queue.data?.requests ?? [];

  return (
    <div className="argus-page">
      <PageHeader
        title="Subject-rights requests (DSAR)"
        description={
          canManage
            ? 'Fulfil data-subject requests. Export sends a PII-safe copy; erasure is refused when a legal hold or a statutory retention floor applies.'
            : 'Data-subject requests. Only a managing partner can fulfil them.'
        }
      />

      {status ? <Notice tone="success">{status}</Notice> : null}
      {error ? (
        <ErrorState
          title="The request was not fulfilled"
          message={error.message}
          requestId={error.requestId}
        />
      ) : null}

      <Card>
        <CardHeader
          title="Fulfilment queue"
          description="Every export, rectification and erasure is recorded in the audit trail."
          actions={
            queue.data ? (
              <Badge tone="muted">
                {rows.length === 1 ? '1 request' : `${String(rows.length)} requests`}
              </Badge>
            ) : null
          }
        />
        <CardBody flush>
          {queue.isPending ? (
            <div className="argus-settings-inset">
              <SkeletonText lines={4} label="Loading DSAR requests" />
            </div>
          ) : queue.isError ? (
            <div className="argus-settings-inset">
              <ErrorState
                title="We couldn't load the requests"
                message={getErrorMessage(queue.error)}
                requestId={getRequestId(queue.error)}
                onRetry={() => void queue.refetch()}
                retrying={queue.isFetching}
              />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              title="No data-subject requests"
              description="Access, rectification and erasure requests appear here for fulfilment."
            />
          ) : (
            <Table aria-label="DSAR requests" dense>
              <thead>
                <tr>
                  <Th>Subject</Th>
                  <Th>Case</Th>
                  <Th>Type</Th>
                  <Th>Status</Th>
                  <Th>Requested</Th>
                  <Th className="argus-settings-row-actions">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <Td mono={!row.subject_email}>{subjectLabel(row)}</Td>
                    <Td mono>{row.case_number}</Td>
                    <Td>{humanise(row.request_type)}</Td>
                    <Td>
                      <Badge tone={STATUS_TONES[row.status] ?? 'neutral'}>
                        {humanise(row.status)}
                      </Badge>
                    </Td>
                    <Td>
                      <time dateTime={row.requested_at}>
                        {formatDate(row.requested_at, { withTime: true })}
                      </time>
                    </Td>
                    <Td className="argus-settings-row-actions">
                      <div className="row">
                        <a
                          href={api.dsar.exportUrl(row.id)}
                          className={buttonClassName({ size: 'sm' })}
                        >
                          <Download size={14} aria-hidden="true" />
                          Export
                        </a>
                        {canManage && row.request_type === 'rectification' ? (
                          <Button
                            size="sm"
                            icon={<Pencil size={14} aria-hidden="true" />}
                            onClick={() => {
                              resetBanners();
                              setRectifyField('full_name');
                              setRectifyValue('');
                              setRectifyFor(row);
                            }}
                          >
                            Rectify
                          </Button>
                        ) : null}
                        {canManage && row.request_type === 'erasure' ? (
                          <Button
                            size="sm"
                            variant="danger-outline"
                            icon={<Trash2 size={14} aria-hidden="true" />}
                            onClick={() => {
                              resetBanners();
                              setEraseFor(row);
                            }}
                          >
                            Erase
                          </Button>
                        ) : null}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      {rectifyFor ? (
        <Modal
          open
          onClose={closeRectify}
          dismissOnOverlay={false}
          title="Rectify subject field"
          description={`${subjectLabel(rectifyFor)} · ${rectifyFor.case_number}`}
          footer={
            <>
              <Button variant="ghost" onClick={closeRectify} disabled={rectifyMutation.isPending}>
                Cancel
              </Button>
              <Button
                type="submit"
                form={rectifyFormId}
                variant="primary"
                loading={rectifyMutation.isPending}
                disabled={rectifyValue.trim() === ''}
              >
                Save rectification
              </Button>
            </>
          }
        >
          <form
            id={rectifyFormId}
            aria-label="Rectify subject field"
            onSubmit={(e) => {
              e.preventDefault();
              if (rectifyValue.trim() === '') return;
              rectifyMutation.mutate({
                requestId: rectifyFor.id,
                field: rectifyField,
                value: rectifyValue,
              });
            }}
          >
            <div className="stack">
              <p>Only the allowlisted identity fields can be corrected.</p>
              <Select
                label="Field"
                value={rectifyField}
                onChange={(e) => setRectifyField(e.target.value as DsarRectifyField)}
              >
                {(Object.keys(RECTIFY_FIELD_LABELS) as DsarRectifyField[]).map((field) => (
                  <option key={field} value={field}>
                    {RECTIFY_FIELD_LABELS[field]}
                  </option>
                ))}
              </Select>
              <Input
                label="New value"
                required
                autoComplete="off"
                type={rectifyField === 'email' ? 'email' : 'text'}
                value={rectifyValue}
                onChange={(e) => setRectifyValue(e.target.value)}
              />
            </div>
          </form>
        </Modal>
      ) : null}

      {eraseFor ? (
        <Modal
          open
          onClose={() => setEraseFor(null)}
          role="alertdialog"
          dismissOnOverlay={false}
          hideClose
          title="Confirm subject erasure"
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => setEraseFor(null)}
                disabled={eraseMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={eraseMutation.isPending}
                onClick={() => eraseMutation.mutate(eraseFor.id)}
              >
                Confirm erasure
              </Button>
            </>
          }
        >
          <p>
            Erase the case-scoped data for <strong>{subjectLabel(eraseFor)}</strong> (
            {eraseFor.case_number})? This is refused if a legal hold or a statutory retention floor
            applies. The action is recorded in the audit trail.
          </p>
        </Modal>
      ) : null}
    </div>
  );
}
